// src/components/admin/EditService.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../../providers/dataProvider";
import ServiceCard from "../services/ServiceCard";
import ImageSelector from "../shared/ImageSelector";
import { pageMeta } from "../../config/helmetConfig";
import { updateMetaTags } from "../../utils/metaManager";

// Main Edit Service Component
export default function EditService() {
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    updateMetaTags(pageMeta.admin);
  }, []);
  // Use the consolidated DataContext with the additional image methods
  const {
    services,
    servicesLoading,
    updateService,
    addService,
    images,
    getImageUrl,
    getImageByKey,
  } = useData();

  const [newService, setService] = useState({
    id: "",
    title: "",
    description: "",
    image_path: "", // This holds the aws_location
  });

  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const saveDisabled =
    saving || saveSuccess || !newService.title || !newService.description;

  useEffect(() => {
    // Only attempt to find the service when loading is complete
    if (id === "new") {
      return;
    }

    if (!servicesLoading && services.length > 0) {
      const foundService = services.find(
        (service) => service.id.toString() === id.toString()
      );

      if (foundService) {
        // Get the aws_location from the image_id
        const image = images.find((img) => img.id === foundService.image_id);
        const imagePath = image ? image.aws_location : "";

        // Update state with the found service data
        setService({
          id: foundService.id,
          title: foundService.title,
          description: foundService.description,
          image_path: imagePath, // Store the aws_location in image_path for the UI
        });
      }
    }
  }, [services, servicesLoading, id, images]);

  // Update preview image URL when image_path changes
  useEffect(() => {
    const updatePreviewUrl = async () => {
      if (newService.image_path) {
        try {
          const url = await getImageUrl(newService.image_path);
          setPreviewImageUrl(url);
        } catch (error) {
          console.error("Error getting image URL:", error);
          setPreviewImageUrl(null);
        }
      } else {
        setPreviewImageUrl(null);
      }
    };

    updatePreviewUrl();
  }, [newService.image_path, getImageUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setService({
      ...newService,
      [name]: value,
    });
  };

  const handleImageChange = (imageKey) => {
    setService({
      ...newService,
      image_path: imageKey,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      // Get the image ID from the path
      const image = getImageByKey(newService.image_path);
      const imageId = image ? image.id : null;

      // Create the data object for the API with the correct field names
      const serviceData = {
        title: newService.title,
        description: newService.description,
        image_id: imageId, // This is what the backend expects
      };

      if (id === "new") {
        await addService(serviceData);
      } else {
        await updateService(id, serviceData);
      }
    } catch (error) {
      console.error("Error updating service:", error);
    } finally {
      setSaving(false);
      setSaveSuccess(true);
      setTimeout(() => {
        navigate("/admin/services");
      }, 2000);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <h1 className="text-light">
            {id === "new" ? "Add" : "Edit"} Service
          </h1>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate("/admin/services")}
          >
            Back to Services
          </Button>
        </Col>
      </Row>
      {saveSuccess && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success">
              Service updated successfully! Redirecting...
            </Alert>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit}>
        <Row>
          {/* Edit Form Section */}
          <Col md={7} className="mb-4">
            <Card
              className="bg-dark text-white border-secondary"
              style={{ width: "100%" }}
            >
              <Card.Header className="bg-dark text-white">
                Service Details
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Service Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={newService.title}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Service Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="description"
                    value={newService.description}
                    onChange={handleInputChange}
                    required
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>

                {/* Replace the old file upload with the ImageSelector component */}
                <ImageSelector
                  label="Service Image"
                  value={newService.image_path}
                  onChange={handleImageChange}
                />

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/admin/services")}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={saveDisabled}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Preview Section */}
          <ServiceCard
            service={{
              ...newService,
              // Pass the preview URL to the ServiceCard component
              image: previewImageUrl,
            }}
          />
        </Row>
      </Form>
    </Container>
  );
}
